import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Search, Save, Trash2, X } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";

const EMPTY = {
  seo_title: "", meta_description: "", canonical: "", noindex: null, og_image: "",
  author: "", reviewer: "", published_at: "", review_due: "", related_links: [], notes: "",
};

function Editor({ row, onClose, onSaved }) {
  const [form, setForm] = useState({ ...EMPTY, ...(row.override || {}) });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/seo/controls", { path: row.path, ...form });
      toast.success("Saved. It applies to the live page straight away.");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    await api.delete("/admin/seo/controls", { params: { path: row.path } });
    toast.success("Override removed — the page is back to what it generates for itself.");
    onSaved();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-navy-deep/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
         data-testid="seo-control-editor">
      <div className="bg-white rounded-2xl w-full max-w-2xl p-6 my-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold text-navy">{row.path}</h3>
            <p className="text-xs text-slate-500 mt-1">
              Blank fields keep what the page generates: “{row.title}”
            </p>
          </div>
          <button onClick={onClose} data-testid="seo-control-close" className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">SEO title</label>
            <Input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)}
                   placeholder={row.title} data-testid="seo-control-title" />
            <p className="text-xs text-slate-500 mt-1">{(form.seo_title || row.title || "").length} characters — Google shows roughly 60.</p>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Meta description</label>
            <Textarea rows={3} value={form.meta_description} onChange={(e) => set("meta_description", e.target.value)}
                      placeholder={row.description} data-testid="seo-control-description" />
            <p className="text-xs text-slate-500 mt-1">{(form.meta_description || row.description || "").length} characters — roughly 160 is shown.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Canonical override</label>
              <Input value={form.canonical} onChange={(e) => set("canonical", e.target.value)}
                     placeholder={row.canonical} data-testid="seo-control-canonical" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Social sharing image</label>
              <Input value={form.og_image} onChange={(e) => set("og_image", e.target.value)}
                     placeholder="Leave blank for the generated card" data-testid="seo-control-og-image" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Search engines</label>
            <div className="flex gap-2 mt-1">
              {[["Automatic", null], ["Index", false], ["Don't index", true]].map(([label, value]) => (
                <button
                  key={label}
                  onClick={() => set("noindex", value)}
                  data-testid={`seo-control-index-${String(value)}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${form.noindex === value ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200"}`}
                >{label}</button>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Automatic follows publication status. A page set to “Don't index” drops out of the sitemap as well.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Author</label>
              <Input value={form.author} onChange={(e) => set("author", e.target.value)} data-testid="seo-control-author" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Reviewer</label>
              <Input value={form.reviewer} onChange={(e) => set("reviewer", e.target.value)} data-testid="seo-control-reviewer" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Published date</label>
              <Input type="date" value={form.published_at} onChange={(e) => set("published_at", e.target.value)} data-testid="seo-control-published" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Content review due</label>
              <Input type="date" value={form.review_due} onChange={(e) => set("review_due", e.target.value)} data-testid="seo-control-review-due" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Related links</label>
            {(form.related_links || []).map((l, i) => (
              <div key={i} className="flex gap-2 mt-2">
                <Input value={l.label || ""} placeholder="Link text"
                       onChange={(e) => set("related_links", form.related_links.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} />
                <Input value={l.href || ""} placeholder="/services/bookkeeping"
                       onChange={(e) => set("related_links", form.related_links.map((x, j) => j === i ? { ...x, href: e.target.value } : x))} />
                <Button variant="outline" onClick={() => set("related_links", form.related_links.filter((_, j) => j !== i))}>
                  <Trash2 size={14} />
                </Button>
              </div>
            ))}
            <Button variant="outline" className="mt-2" data-testid="seo-control-add-link"
                    onClick={() => set("related_links", [...(form.related_links || []), { label: "", href: "" }])}>
              Add a related link
            </Button>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Notes (internal)</label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="seo-control-notes" />
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 mt-6">
          {row.has_override
            ? <Button variant="outline" onClick={clear} data-testid="seo-control-clear">Remove override</Button>
            : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="seo-control-save">
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save className="mr-2" size={16} />} Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SeoControlsPanel() {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/admin/seo/controls", { params: { q, kind } });
    setRows(data.rows || []);
    setCount(data.row_count || 0);
    setLoading(false);
  }, [q, kind]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="space-y-4" data-testid="seo-controls-panel">
      <p className="text-sm text-slate-600">
        Every public URL, with what is currently being served. Fill a field in to override it; leave it blank and the
        page keeps generating its own.
      </p>
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search by URL or title" data-testid="seo-controls-search" />
        </div>
        <select value={kind} onChange={(e) => setKind(e.target.value)}
                className="text-sm border border-slate-200 rounded-md px-2 py-2" data-testid="seo-controls-kind">
          <option value="">All pages</option>
          <option value="page">Site pages</option>
          <option value="ask-cassie">Ask Cassie</option>
        </select>
        <span className="text-xs text-slate-500">{count} pages</span>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={16} /> Loading…</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full text-sm" data-testid="seo-controls-table">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">URL</th>
                <th className="text-left px-3 py-2">Title being served</th>
                <th className="text-left px-3 py-2">Index</th>
                <th className="text-left px-3 py-2">Override</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.path} className="border-t border-slate-100">
                  <td className="px-3 py-2 whitespace-nowrap">{r.path}</td>
                  <td className="px-3 py-2 text-slate-600">{r.title}</td>
                  <td className="px-3 py-2">{r.noindex ? <span className="text-amber-700">No</span> : "Yes"}</td>
                  <td className="px-3 py-2">{r.has_override ? <span className="text-emerald-700 font-semibold">Set</span> : <span className="text-slate-400">—</span>}</td>
                  <td className="px-3 py-2 text-right">
                    <Button variant="outline" size="sm" onClick={() => setEditing(r)}
                            data-testid={`seo-control-edit-${r.path}`}>Edit</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <Editor row={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}
